import { Chip, Grid, Skeleton, TablePagination, TableSortLabel, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentIcons } from 'src/pages/therapist-dashboard/dashboard-icon';
import emptyImg from 'src/pages/therapist-dashboard/therapist-document/image-removebg-preview.png';
import { TableHeaderMultiselect } from 'src/pages/therapist-dashboard/therapist-document/table-hearder-multiselect';
import MultiSelectWithSearch from 'src/pages/therapist-dashboard/therapist-document/table-header-multiselect-search';
import {
  getPhysicianSignaturePendingAction,
  getPendingSignaturePhysiciansAction,
} from 'src/redux/actions/departmentDashboard';
import { getAllPatientsByAction } from 'src/redux/actions/patientAction';
import moment from 'moment';

const sortKeyMap: Record<string, string> = {
  patientName: 'patient_name',
  documentType: 'document_type',
  certStartDate: 'cert_start_date',
  countdown: 'countdown_days',
  physician: 'physician_name',
};

const defaultSortDirection: Record<string, 'asc' | 'desc'> = {
  certStartDate: 'desc',
  countdown: 'asc',
};

const PhysicianSignaturePending: FC = () => {
  const dispatch = useDispatch();

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'countdown',
    direction: 'asc',
  });
  const [selectedDocumentType, setSelectedDocumentType] = useState<any>([
    { label: 'Evaluation', value: 'EVALUATION' },
    { label: 'Recertification', value: 'RECERTIFICATION' },
  ]);
  const [selectedPatient, setSelectedPatient] = useState<any>([]);
  const [selectedPhysician, setSelectedPhysician] = useState<any>([]);
  const [pageNo, setPageNo] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const pendingSignatures = useSelector((state: any) => state.getPhysicianSignaturePendingReducer);
  const data = pendingSignatures?.response?.content || pendingSignatures?.response || [];
  const totalCount = pendingSignatures?.response?.totalElements ?? data?.length ?? 0;
  const loading = pendingSignatures?.loading;

  const totalPatientRes = useSelector((state: any) => state.getAllPatientsReducer);
  const allPhysiciansRes = useSelector((state: any) => state.getPendingSignaturePhysiciansReducer);

  useEffect(() => {
    dispatch(
      getAllPatientsByAction({
        data: { searchString: '' },
        params: { page: 0, pageSize: 20, active: true },
      })
    );

    dispatch(getPendingSignaturePhysiciansAction());
  }, []);

  const patientOptions = useMemo(() => {
    return totalPatientRes?.totalPatients?.map((res: any) => ({
      label: `${res?.lastName}, ${res?.firstName}`,
      value: res?.uuid,
    }));
  }, [totalPatientRes]);

  const physicianOptions = useMemo(() => {
    return allPhysiciansRes?.response
      ?.map((res: any) => ({
        label: `${res?.physicianName}`,
        value: res?.uuid,
      }))
      ?.sort((a: any, b: any) => a?.label?.localeCompare(b?.label));
  }, [allPhysiciansRes]);

  const fetchData = () => {
    const params: any = {};

    if (selectedPatient?.length > 0) {
      params.patientUuid = selectedPatient?.map((p: any) => p.value).join(',');
    }
    if (selectedPhysician?.length > 0) {
      params.physicianUuid = selectedPhysician?.map((p: any) => p.value).join(',');
    }
    if (selectedDocumentType?.length > 0) {
      params.documentTypes = selectedDocumentType.map((d: any) => d.value).join(', ');
    }
    if (sortConfig.key) {
      params.sortBy = sortKeyMap[sortConfig.key] || sortConfig.key;
      params.sortDirection = sortConfig.direction;
    }

    params.page = page;
    params.pageSize = rowsPerPage;

    dispatch(getPhysicianSignaturePendingAction(params));
  };

  useEffect(() => {
    fetchData();
  }, [sortConfig, selectedDocumentType, selectedPatient, selectedPhysician, page, rowsPerPage]);

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      // Toggle direction when re-clicking the active column; otherwise switch
      // to the new column with its column-specific default direction so the
      // first click always produces a visible reorder. Clearing `prev` here
      // also guarantees only one column shows an active sort indicator at a
      // time, addressing THIN-7080.
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: defaultSortDirection[key] ?? 'asc' };
    });
  };

  const handleSortMultiselect = (label: any, direction: any) => {
    if (label === 'Document Type') return setSortConfig({ key: 'documentType', direction });
    if (label === 'PatientName') return setSortConfig({ key: 'patientName', direction });
    if (label === 'Physician') return setSortConfig({ key: 'physician', direction });
  };

  const handleClearFilterColumn = (column: 'patient' | 'documentType' | 'physician') => () => {
    if (column === 'patient') setSelectedPatient([]);
    if (column === 'documentType') setSelectedDocumentType([]);
    if (column === 'physician') setSelectedPhysician([]);
    setSortConfig({ key: 'countdown', direction: 'asc' });
    setPage(0);
  };

  const getCountdownDisplay = (countdown: number | null | undefined, documentType?: string) => {
    const isDischarge = (documentType || '').toString().toUpperCase() === 'DISCHARGE';
    if (isDischarge || countdown === null || countdown === undefined) {
      return {
        text: '-',
        color: 'inherit',
        fontWeight: 400,
      };
    }
    if (countdown < 0) {
      return {
        text: `${countdown} (Overdue)`,
        color: '#F04438',
        fontWeight: 700,
      };
    }
    if (countdown === 0) {
      return {
        text: '0',
        color: '#F04438',
        fontWeight: 700,
      };
    }
    if (countdown >= 1 && countdown <= 5) {
      return {
        text: `${countdown}`,
        color: '#F79009',
        fontWeight: 700,
      };
    }
    return {
      text: `${countdown}`,
      color: '#111927',
      fontWeight: 700,
    };
  };

  const formatDate = (dateStr: string | undefined | null) => {
    if (!dateStr) return '-';

    // To make it appropriate across timezones
    return moment(
      new Date(new Date(dateStr)?.valueOf() + new Date(dateStr)?.getTimezoneOffset() * 60 * 1000)
    ).format('MMM DD, YYYY');
  };

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Stack sx={{ flexShrink: 0 }}>
        <Grid
          display={'flex'}
          alignItems={'center'}
          marginBottom={1}
        >
          <Typography variant="h6">Physician Signature Pending</Typography>
          <Chip
            label={`${totalCount} Document`}
            variant="outlined"
            sx={{ marginLeft: '10px' }}
          />
        </Grid>
      </Stack>
      <Stack
        mt={3}
        sx={{ flex: 1, minHeight: 0 }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '& > table': {
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              tableLayout: 'fixed',
            },
            '& thead, & tbody tr': {
              display: 'table',
              width: '100%',
              tableLayout: 'fixed',
            },
            '& tbody': {
              display: 'block',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
            },
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    hideSortIcon
                    sx={{ cursor: 'default', '&:hover': { color: 'inherit' } }}
                  >
                    Patient
                  </TableSortLabel>
                  <MultiSelectWithSearch
                    options={patientOptions}
                    pageNo={pageNo}
                    setPageNo={setPageNo}
                    handleSortMultiselect={handleSortMultiselect}
                    setAppliedValues={setSelectedPatient}
                    appliedValues={selectedPatient}
                    sortConfig={sortConfig}
                    onClearAll={handleClearFilterColumn('patient')}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    hideSortIcon
                    sx={{ cursor: 'default', '&:hover': { color: 'inherit' } }}
                  >
                    Document Type
                  </TableSortLabel>
                  <TableHeaderMultiselect
                    label="Document Type"
                    sortConfig={sortConfig}
                    appliedValues={selectedDocumentType}
                    setAppliedValues={setSelectedDocumentType}
                    options={[
                      { label: 'Evaluation', value: 'EVALUATION' },
                      { label: 'Discharge', value: 'DISCHARGE' },
                      { label: 'Recertification', value: 'RECERTIFICATION' },
                    ]}
                    handleSortMultiselect={handleSortMultiselect}
                    onClearAll={handleClearFilterColumn('documentType')}
                  />
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'certStartDate'}
                    direction={sortConfig.key === 'certStartDate' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('certStartDate')}
                  >
                    Cert Start Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'countdown'}
                    direction={sortConfig.key === 'countdown' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('countdown')}
                  >
                    30-Day Countdown
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    hideSortIcon
                    sx={{ cursor: 'default', '&:hover': { color: 'inherit' } }}
                  >
                    Physician
                  </TableSortLabel>
                  <TableHeaderMultiselect
                    label="Physician"
                    sortConfig={sortConfig}
                    appliedValues={selectedPhysician}
                    setAppliedValues={setSelectedPhysician}
                    options={physicianOptions || []}
                    handleSortMultiselect={handleSortMultiselect}
                    onClearAll={handleClearFilterColumn('physician')}
                  />
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    {Array.from({ length: 9 }).map((_, index) => (
                      <Box
                        textAlign="center"
                        key={index}
                        p={0.8}
                        sx={{ minWidth: '530px' }}
                      >
                        <Typography
                          variant="h3"
                          color="initial"
                        >
                          <Skeleton animation="wave" />
                        </Typography>
                      </Box>
                    ))}
                  </TableCell>
                </TableRow>
              ) : (Array.isArray(data) ? data : []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box
                      textAlign="center"
                      p={3}
                    >
                      <img
                        src={emptyImg}
                        alt="loader"
                        style={{
                          width: 620,
                          height: 240,
                          margin: 'auto',
                          alignItems: 'center',
                          alignContent: 'center',
                          marginTop: 50,
                          display: 'block',
                        }}
                      />
                      <Typography
                        color={'text.secondary'}
                        variant="h6"
                        fontWeight={'200'}
                      >
                        No documents pending physician signature.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                (Array.isArray(data) ? data : [])?.map((row: any, index: number) => {
                  // Pass the raw countdown (which may be null for Discharge per
                  // BE AC5) plus the document type so getCountdownDisplay can
                  // render "-" instead of falling back to 0. See THIN-7111.
                  const rawCountdown = row?.countdown ?? row?.countdownDays ?? null;
                  const countdown = getCountdownDisplay(
                    rawCountdown,
                    row?.documentType || row?.formType
                  );
                  return (
                    <TableRow
                      hover
                      key={index}
                    >
                      <TableCell>
                        <Typography variant="subtitle2">{row.patientName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack
                          alignItems="center"
                          direction="row"
                          spacing={1}
                        >
                          <Grid
                            container
                            sx={{ alignItems: 'center' }}
                          >
                            <Box
                              sx={{
                                marginRight: '4px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <DocumentIcons
                                iconType={(row?.formType || row?.documentType)
                                  ?.split('_')?.[0]
                                  ?.toLowerCase()}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2">{row?.documentType}</Typography>
                            </Box>
                          </Grid>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {formatDate(row?.certPeriodStartDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: countdown?.color, fontWeight: countdown?.fontWeight }}
                        >
                          {countdown?.text}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {row?.physician || row?.physicianName}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
        {(Array.isArray(data) ? data : []).length !== 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default PhysicianSignaturePending;
